import {useCallback, useEffect, useMemo, useState} from "react";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import {ChevronDownIcon} from "@heroicons/react/16/solid";
import {notify} from "@/components/Notifications";
import {Banner, useSaveBanner} from "@/hooks/useBanners";
import {localInputToUtcIso, toLocalInput} from "@/lib/utils.ts";
import {getErrMsg} from "@/lib/errors.ts";

type Props = {
    open: boolean;
    initial?: Partial<Banner> | null;
    onClose: () => void;
};

const BANNER_TYPES = ["ADVERTISEMENT"] as const;
const BANNER_PLACEMENTS = ["HOME_MIDDLE", "HOME_BOTTOM", "ARTICLE_TOP", "ARTICLE_INLINE", "ARTICLE_BOTTOM", "SIDEBAR_TOP", "SIDEBAR_MIDDLE", "SIDEBAR_BOTTOM"] as const;

function pickExistingImage(a?: unknown): { id?: string; url?: string } {
    const x = a as any;
    if (!x) return {};
    if (x.imageUrl) return {id: x?.id, url: x.imageUrl};
    return {};
}

export default function BannerUpsertDialog({open, initial, onClose}: Props) {
    const isEdit = Boolean(initial?.id);

    // Core fields
    const [title, setTitle] = useState(initial?.title ?? "");
    const [type, setType] = useState<string>((initial as any)?.type ?? "ADVERTISEMENT");
    const [placement, setPlacement] = useState<string>((initial as any)?.placement ?? "HOME_MIDDLE");
    const [targetUrl, setTargetUrl] = useState<string>((initial as any)?.targetUrl ?? "");

    const [startAt, setStartAt] = useState<string>(initial?.startAt);
    const [endAt, setEndAt] = useState<string>(initial?.endAt);
    const [enabled, setEnabled] = useState<boolean>((initial as any)?.enabled ?? true);

    // File handling
    const [file, setFile] = useState<File | null>(null);
    const [uploadPct, setUploadPct] = useState(0);
    const [touched, setTouched] = useState(false);

    // Existing media / deletion
    const [removedExisting, setRemovedExisting] = useState(false);
    const existing = useMemo(() => pickExistingImage(initial), [initial]);
    const existingImageUrl = removedExisting ? undefined : existing.url;

    // Preview
    const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : undefined), [file]);
    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    // Upload constraints
    const MAX_MB = 10;
    const ALLOWED_TYPES = useMemo(
        () => new Set(["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"]),
        []
    );
    const acceptAttr = useMemo(() => Array.from(ALLOWED_TYPES).join(","), [ALLOWED_TYPES]);

    const {mutateAsync: save, isPending} = useSaveBanner();

    useEffect(() => {
        if (open) {
            setTitle(initial?.title ?? "");
            setType((initial as any)?.type ?? "ADVERTISEMENT");
            setPlacement((initial as any)?.placement ?? "HOME_MIDDLE");
            setTargetUrl(initial?.targetUrl ?? "");
            setStartAt(toLocalInput(initial?.startAt));
            setEndAt(toLocalInput(initial?.endAt));
            setEnabled(initial?.enabled ?? true);
            setFile(null);
            setUploadPct(0);
            setRemovedExisting(false);
            setTouched(false);
        }
    }, [open, initial]);

    const onPickFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
            const f = e.target.files?.[0];
            if (!f) return;
            if (!ALLOWED_TYPES.has(f.type)) {
                notify.error(`Invalid file type. Allowed: ${Array.from(ALLOWED_TYPES).join(", ")}`);
                return;
            }
            if (f.size > MAX_MB * 1024 * 1024) {
                notify.error(`File too large. Max size is ${MAX_MB}MB.`);
                return;
            }
            setFile(f);
        },
        [ALLOWED_TYPES]
    );

    const errors = useMemo(() => {
        const e: Record<string, string> = {};
        if (!title.trim()) e.title = "Title is required.";
        if (!type) e.type = "Type is required.";
        if (!placement) e.placement = "Placement is required.";

        const needsFile = !isEdit || removedExisting;
        if (needsFile && !file) e.file = "Please choose an image file.";

        if (!startAt) e.startAt = "Start date is required.";
        if (!endAt) e.endAt = "End date is required.";

        if (startAt && endAt && new Date(startAt) > new Date(endAt)) {
            e.schedule = "End date must be after start date.";
        }
        if (targetUrl && !/^https?:\/\//i.test(targetUrl)) {
            e.targetUrl = "Enter a valid URL starting with https://";
        }
        return e;
    }, [title, type, placement, isEdit, removedExisting, file, startAt, endAt, targetUrl]);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (Object.keys(errors).length) return;

        try {
            await save({
                id: initial?.id,
                title,
                type,
                placement,
                file,
                targetUrl,
                startAt: localInputToUtcIso(startAt),
                endAt: localInputToUtcIso(endAt),
                enabled,
                onUploadProgress: setUploadPct,
            })
            setUploadPct(0);
            notify.success(`Banner ${isEdit ? "updated" : "created"} successfully.`);
            onClose();
        } catch (e) {
            notify.error(getErrMsg(e, `Failed to ${isEdit ? "update" : "create"} banner. Please try again.`));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} className="relative z-10">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-800/75 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in"
            />
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative w-full max-w-md sm:max-w-lg md:max-w-xl transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in sm:my-8 sm:p-6 sm:data-[closed]:translate-y-0 sm:data-[closed]:scale-95"
                    >
                        <DialogTitle as="h3" className="text-lg font-semibold text-blue-900">
                            {isEdit ? "Edit Banner" : "Add Banner"}
                        </DialogTitle>

                        <form onSubmit={onSubmit} noValidate>
                            <div className="mt-6 grid grid-cols-1 gap-6">
                                {/* Title */}
                                <div>
                                    <label htmlFor="title"
                                           className="block text-sm font-medium text-blue-900">
                                        Title
                                    </label>
                                    <p className="mt-1 text-xs text-gray-500">Internal name for the banner.</p>
                                    <input
                                        id="title"
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        required
                                        aria-invalid={touched && !!errors.title}
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                    {touched && errors.title && (
                                        <p className="mt-1 text-xs text-red-600">{errors.title}</p>
                                    )}
                                </div>

                                {/* Type & Placement */}
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <div>
                                        <label htmlFor="type"
                                               className="block text-sm font-medium text-blue-900">Type</label>
                                        <div className="mt-2 relative">
                                            <select
                                                id="type"
                                                value={type}
                                                onChange={(e) => setType(e.target.value)}
                                                className="w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                            >
                                                {BANNER_TYPES.map((t) => (
                                                    <option key={t} value={t}>{t}</option>
                                                ))}
                                            </select>
                                            <ChevronDownIcon aria-hidden="true"
                                                             className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 size-5 text-gray-500 sm:size-4"/>
                                        </div>
                                        {touched && errors.type && (
                                            <p className="mt-1 text-xs text-red-600">{errors.type}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label htmlFor="placement"
                                               className="block text-sm font-medium text-blue-900">Placement</label>
                                        <div className="mt-2 relative">
                                            <select
                                                id="placement"
                                                value={placement}
                                                onChange={(e) => setPlacement(e.target.value)}
                                                className="w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                            >
                                                {BANNER_PLACEMENTS.map((p) => (
                                                    <option key={p} value={p}>{p}</option>
                                                ))}
                                            </select>
                                            <ChevronDownIcon aria-hidden="true"
                                                             className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 size-5 text-gray-500 sm:size-4"/>
                                        </div>
                                        {touched && errors.placement && (
                                            <p className="mt-1 text-xs text-red-600">{errors.placement}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Schedule */}
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <div>
                                        <label htmlFor="startAt"
                                               className="block text-sm font-medium text-blue-900">Start</label>
                                        <input
                                            id="startAt"
                                            type="datetime-local"
                                            value={startAt}
                                            onChange={(e) => setStartAt(e.target.value)}
                                            className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                        />
                                        {touched && errors.startAt && (
                                            <p className="mt-1 text-xs text-red-600">{errors.startAt}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label htmlFor="endAt"
                                               className="block text-sm font-medium text-blue-900">End</label>
                                        <input
                                            id="endAt"
                                            type="datetime-local"
                                            value={endAt}
                                            onChange={(e) => setEndAt(e.target.value)}
                                            className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                        />
                                        {touched && errors.endAt && (
                                            <p className="mt-1 text-xs text-red-600">{errors.endAt}</p>
                                        )}
                                    </div>
                                    {touched && errors.schedule && (
                                        <p className="sm:col-span-2 mt-1 text-xs text-red-600">{errors.schedule}</p>
                                    )}
                                </div>

                                {/* Enabled */}
                                <div className="flex items-center gap-3">
                                    <input
                                        id="enabled"
                                        type="checkbox"
                                        checked={enabled}
                                        onChange={(e) => setEnabled(e.target.checked)}
                                        className="size-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                    />
                                    <label htmlFor="enabled" className="text-sm font-medium text-blue-900">
                                        Enabled (show banner when within schedule)
                                    </label>
                                </div>

                                {/* Image */}
                                <div>
                                    <label htmlFor="file"
                                           className="block text-sm font-medium text-blue-900">
                                        Image
                                    </label>

                                    {(previewUrl || existingImageUrl) && (
                                        <div className="mt-2">
                                            <img
                                                src={previewUrl || existingImageUrl}
                                                alt="Banner image"
                                                className="h-full w-full rounded-md object-cover ring-1 ring-gray-200"
                                            />
                                        </div>
                                    )}
                                    <div className="mt-3 flex flex-wrap items-center gap-3">
                                        <input
                                            id="file"
                                            name="file-upload"
                                            type="file"
                                            accept={acceptAttr}
                                            onChange={onPickFile}
                                            className="block w-full sm:w-auto text-sm text-gray-700 file:mr-4 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100"
                                        />
                                        {file && (
                                            <button
                                                type="button"
                                                onClick={() => setFile(null)}
                                                className="text-sm text-gray-600 hover:text-blue-900"
                                            >
                                                Clear selection
                                            </button>
                                        )}
                                    </div>

                                    {touched && errors.file && (
                                        <p className="mt-1 text-xs text-red-600">{errors.file}</p>
                                    )}

                                    {uploadPct > 0 && uploadPct < 100 && (
                                        <div className="mt-3 h-2 w-full overflow-hidden rounded bg-gray-100">
                                            <div className="h-full bg-blue-900 transition-all"
                                                 style={{width: `${uploadPct}%`}}/>
                                        </div>
                                    )}
                                    <p className="mt-1 text-xs text-gray-500">PNG, JPG up to {MAX_MB}MB.</p>
                                </div>

                                {/* Target URL */}
                                <div>
                                    <label htmlFor="targetUrl" className="block text-sm font-medium text-blue-900">Target
                                        URL (optional)</label>
                                    <input
                                        id="targetUrl"
                                        type="url"
                                        value={targetUrl}
                                        onChange={(e) => setTargetUrl(e.target.value)}
                                        placeholder="https://example.com/landing"
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                    {touched && errors.targetUrl && (
                                        <p className="mt-1 text-xs text-red-600">{errors.targetUrl}</p>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="mt-2 flex items-center justify-end gap-x-3">
                                    <button type="button" onClick={onClose}
                                            disabled={isPending}
                                            className="text-sm font-semibold text-blue-900">
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isPending}
                                        className="inline-flex items-center rounded-md bg-blue-900 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 disabled:opacity-60"
                                    >
                                        {isPending ? (isEdit ? "Saving…" : "Creating…") : isEdit ? "Save" : "Create"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
